import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest } from 'rxjs';
import { delay } from 'rxjs/operators';
import { Router } from '@angular/router';

import { AuthModal } from '@shared/models/auth';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { selectAuthUser, selectAuthModal } from '@state/auth/auth.selectors';
import { setAuthModal } from '@state/auth/auth.actions';
import { hidePromoteModalAction } from '@state/config/config.actions';
import { hiddenPromoteModals } from '@state/config/config.selectors';

/**
 * Usage instructions:
 * - Provide a unique `promoteSlug` so dismissals persist via `hiddenPromoteModals`.
 * - Adjust `hideAfterDate` to the last day the promotion should display.
 * - Tweak `requireAuth` / `requireFullAccountSetup` to control which users see it.
 */
@UntilDestroy()
@Component({
  selector: 'app-promote-modal',
  templateUrl: './promote-modal.component.html',
  styleUrls: ['./promote-modal.component.scss'],
})
export class PromoteModalComponent implements OnInit {
  // Customize modal behavior here
  protected promoteSlug = 'free-buyers-list-blast';
  protected requireAuth = false;
  protected requireFullAccountSetup = false;
  // Hide after: Year, Month (0-indexed), Day
  protected hideAfterDate = new Date(2025, 9, 10);

  // Don't change the following properties
  protected open = false;
  protected isLoggedIn = false;

  constructor(
    private store: Store<AppState>,
    private router: Router
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.store.select(hiddenPromoteModals),
      this.store.select(selectAuthUser),
      this.store.select(selectAuthModal),
    ])
      .pipe(
        // Show after a short delay for better UX
        delay(2000),
        untilDestroyed(this)
      )
      .subscribe(([hiddenModalSlugs, user, authModal]) => {
        this.isLoggedIn = !!user;

        if (hiddenModalSlugs?.includes(this.promoteSlug)) {
          this.open = false;
          return;
        }

        const currentDate = new Date();
        this.hideAfterDate.setHours(23, 59, 59, 999); // Set to end of day in local timezone
        let shouldShowThisModal = currentDate <= this.hideAfterDate;

        if (shouldShowThisModal && this.requireAuth) {
          if (!user) shouldShowThisModal = false;

          if (this.requireFullAccountSetup) {
            if (!user?.preference || !user?.preference.roles) {
              shouldShowThisModal = false;
            }
          }
        }

        if (authModal) {
          shouldShowThisModal = false;
        }

        this.open = shouldShowThisModal;
      });
  }

  redirectAction(): void {
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'promoteEmailBlastActionClick' });
    }

    this.dismissModal();
    setTimeout(() => {
      this.router.navigate(['/upload-buyers']);
    }, 500);
  }

  openAuthModal(modal: AuthModal): void {
    this.dismissModal();
    this.store.dispatch(setAuthModal({ authModal: modal }));
  }

  dismissModal(triggerEvent = false): void {
    if (triggerEvent && window.dataLayer) {
      window.dataLayer.push({ event: 'promoteEmailBlastDismissClick' });
    }

    this.open = false;
    this.store.dispatch(
      hidePromoteModalAction({ promoteModalSlug: this.promoteSlug })
    );
  }
}
